import Client from 'shopify-buy';
import { store } from './react-initialize';
import * as Sentry from '@sentry/browser';

/* Translators: %1 is an email address */
const CHECKOUT_UNAVAILABLE_MESSAGE = _js(
   'Checkout unavailable. Please contact %1.',
   'support@ifixit.com'
);
/* Translators: %1 -> link; %2 -> link; */
const INVALID_EMAIL_MESSAGE = _js(
   "We're having a problem communicating with your account's email address. Please contact our Pro \
    Wholesale support team at %1 or %2 so we can reapply your discount.",
   '<a href="mailto:pro@ifixit.com">pro@ifixit.com</a>',
   '<a href="tel:8055406339">(805) 540-6339</a>'
);
/* Translators: %1 is an email address */
const ITEM_PROBLEMS_PREFIX = _js(
   'Checkout error. Please contact %1. Products: ',
   'support@ifixit.com'
);
const INVALID_EMAIL = 'Invalid email';

export default function checkoutOnShopify() {
   const products = store.getState().headerState.miniCart.products;

   if (App.shopifyCheckout.useDraftOrder) {
      checkoutWithDraftOrder(products);
   } else {
      checkoutWithShopifyBuy(products);
   }
}

function checkoutWithDraftOrder(products) {
   const createCheckout = () => {
      const possiblyRejectedPromise = validateProductsExistOnShopify(products);
      if (possiblyRejectedPromise) {
         return possiblyRejectedPromise;
      }

      return new Request.API_2_0('cart/order/draftOrder', {
         method: 'POST',
         onSuccess: function (response) {
            redirectWithSSO(response.invoiceUrl);
         },
         onFailure: function (xhr, response) {
            if (response !== INVALID_EMAIL) {
               Sentry.captureMessage('Shopify Pro Draft Order Checkout API Failure', {
                  extra: {
                     'Products in Cart': products,
                     Response: response,
                     XMLHttpRequest: xhr,
                  },
               });
            }
            console.error(new Error(response));
         },
      })
         .send()
         .catch(e => {
            if (e === INVALID_EMAIL) {
               Modal.alert(INVALID_EMAIL_MESSAGE);
               return Promise.resolve();
            } else {
               return Promise.reject({
                  failure: true,
                  message: CHECKOUT_UNAVAILABLE_MESSAGE,
               });
            }
         });
   };

   showLoadingIndicator(createCheckout());
}

function checkoutWithShopifyBuy(products) {
   const createCheckout = () => {
      const possiblyRejectedPromise = validateProductsExistOnShopify(products);
      if (possiblyRejectedPromise) {
         return possiblyRejectedPromise;
      }

      const client = Client.buildClient(App.shopifyCheckout.clientDetails);
      const checkoutLineItems = {
         lineItems: products.map(product => ({
            variantId: product.variantId,
            quantity: product.quantity,
         })),
      };
      const attemptCreateCheckout = () =>
         client.checkout.create(checkoutLineItems).then(checkout => {
            const checkoutUrl = new URL(checkout.webUrl);
            const ssoUrl = new URL(App.shopifyCheckout.ssoRoute);
            ssoUrl.searchParams.set('return_to', checkoutUrl);

            window.location = App.isLoggedIn ? ssoUrl : checkoutUrl;
         });

      const handleCheckoutError = e => {
         const invalidProducts = getInvalidProducts(e, products);
         e.message = 'Checkout Error: ' + e.message;
         if (invalidProducts.length) {
            Sentry.captureException(e, {
               extra: {
                  'Invalid Variants in Cart': invalidProducts,
               },
            });
            const joinedSkus = invalidProducts.map(v => v.sku).join(', ');
            const error = new Error(ITEM_PROBLEMS_PREFIX + joinedSkus);
            console.error(error);
            throw error;
         } else {
            Sentry.captureException(e, {
               extra: {
                  'Shopify Buy Create Checkout Input': checkoutLineItems,
               },
            });
            const error = new Error(CHECKOUT_UNAVAILABLE_MESSAGE);
            console.error(error);
            throw error;
         }
      };

      return attemptCreateCheckout().catch(e => {
         const fetchErrorMessages = [
            // Chromium
            'Failed to fetch',
            // Firefox
            'NetworkError when attempting to fetch resource.',
         ];
         if (fetchErrorMessages.includes(e.message)) {
            const retryCreateCheckout = () => attemptCreateCheckout().catch(handleCheckoutError);
            return executeAfter(retryCreateCheckout, 1000);
         } else {
            handleCheckoutError(e);
         }
      });
   };

   showLoadingIndicator(createCheckout());
}

function validateProductsExistOnShopify(products) {
   const nonExistentProducts = products.filter(product => !product.variantId);
   if (nonExistentProducts.length) {
      const joinedSkus = nonExistentProducts.map(product => product.sku).join(', ');
      Sentry.captureMessage("Some cart products don't exist on shopify", {
         extra: {
            'Non-existent products': nonExistentProducts,
         },
      });
      const rejectedPromise = Promise.reject(ITEM_PROBLEMS_PREFIX + joinedSkus);
      console.error(rejectedPromise);
      return rejectedPromise;
   }
}

function redirectWithSSO(returnToUrlString) {
   const url = new URL(App.shopifyCheckout.ssoRoute);
   url.searchParams.set('return_to', returnToUrlString);
   window.location = url;
}

function showLoadingIndicator(promise) {
   LoadingIndicator.withPromise(promise, {
      pendingMessage: _js('Creating checkout'),
      useErrorResponseAsFailureMessage: true,
      hideSuccessMessage: true,
      duration: 10000,
   });
}

/**
 * Returns an array of products that Shopify rejected if there was an
 * issue during checkout creation.
 *
 * @param e The exception thrown if Shopify rejects checkout creation
 * @param products The array of products in the user's cart
 * @return array of rejected products
 */
function getInvalidProducts(e, products) {
   try {
      const invalidVariantMessage = 'Variant is invalid';
      const responses = JSON.parse(e.message);
      return responses.reduce((filteredResponses, response) => {
         if (response.message === invalidVariantMessage) {
            const index = parseInt(response.field[2]);
            if (!isNaN(index)) {
               filteredResponses.push(products[index]);
            }
         }
         return filteredResponses;
      }, []);
   } catch (err) {
      return [];
   }
}

function executeAfter(callback, ms) {
   const delay = new Promise(resolve => setTimeout(resolve, ms));
   return delay.then(callback);
}
